import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import BulkAddModal from "app/pages/sales-intelligence/common-components/modals/BulkAddModal/BulkAddModal";
import {
    INDUSTRIES_CODE_NAMES,
    type IndustryCodeDto,
    industryCodeDtoMap,
} from "app/pages/sales-intelligence/domain/industry/types/codes";
import type { IndustryCodes } from "app/pages/sales-intelligence/types";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import type { IndustryCodesFilterValue } from "../../../../models/IndustryCodesFilter/IndustryCodesFilterValue";
import industryCodesFilterTrackingService from "../../../../services/tracking/industry-codes-filter/trackingService";
import leadsSearchFiltersI18nKeys from "../../../../utils/i18n";

import { parseIndustryBulkValue } from "./utils";

type IndustryCodesBulkAddProps = {
    codeType: IndustryCodeDto;
    value: IndustryCodesFilterValue;
    inclusion: InclusionOption;
    onUpdate: (value: ReturnType<IndustryCodesFilterValue["with"]>) => void;
    sicCodes: readonly IndustryCodes[];
    naicsCodes: readonly IndustryCodes[];
};

const IndustryCodesBulkAdd = ({
    value,
    codeType,
    inclusion,
    onUpdate,
    sicCodes,
    naicsCodes,
}: IndustryCodesBulkAddProps) => {
    const { translate } = useTranslate();

    const [naicsCodesSet, sicCodesSet] = useMemo(
        () => [new Set(naicsCodes.map(({ code }) => code)), new Set(sicCodes.map(({ code }) => code))],
        [naicsCodes, sicCodes],
    );

    const handleOpenModal = () => {
        industryCodesFilterTrackingService.trackIndustryCodesBulkAddClick(INDUSTRIES_CODE_NAMES[codeType]);
    };

    const handleModalClose = () => {
        industryCodesFilterTrackingService.trackIndustryCodesBulkAddModalButtonClick(
            INDUSTRIES_CODE_NAMES[codeType],
            "cancel",
        );
    };

    const handleAdd = (parsedValue: string[]) => {
        const parsedCodes = parsedValue.filter((code) => {
            if (codeType === industryCodeDtoMap.naics) {
                return naicsCodesSet.has(code);
            }

            return sicCodesSet.has(code);
        });

        onUpdate(
            value.with({
                inclusion,
                [codeType]: Array.from(
                    new Set([
                        ...(codeType === industryCodeDtoMap.naics ? value.naicsCodes : value.sicCodes),
                        ...parsedCodes,
                    ]),
                ),
            }),
        );

        industryCodesFilterTrackingService.trackIndustryCodesBulkAddModalButtonClick(
            INDUSTRIES_CODE_NAMES[codeType],
            "add",
        );
    };

    const headerTranslatable = leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalHeader(
        INDUSTRIES_CODE_NAMES[codeType],
    );
    const textTranslatable = leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalText(
        INDUSTRIES_CODE_NAMES[codeType],
    );

    return (
        <div>
            <BulkAddModal
                parseInputValue={parseIndustryBulkValue}
                header={translate(headerTranslatable.key, headerTranslatable.replacements)}
                text={translate(textTranslatable.key, textTranslatable.replacements)}
                counterText={`${INDUSTRIES_CODE_NAMES[codeType]} ${translate(leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalCounter())}`}
                placeholder={translate(leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalPlaceholder())}
                buttonText={translate(leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddButton())}
                onOpen={handleOpenModal}
                onClose={handleModalClose}
                onAddValues={handleAdd}
            />
        </div>
    );
};

export default IndustryCodesBulkAdd;
